import React from "react";
import {
  Box,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery
} from "@mui/material";

import ResponsiveTableWrapper from "./ResponsiveTableWrapper";
import { contentCardSx, mobileActionGroupSx, textWrapSx } from "../utils/adminResponsive";

const MOBILE_BREAKPOINT_QUERY = "(max-width:767.95px)";

const getColumnValue = (column, row, index, isMobile) => {
  const renderer = isMobile ? column.mobileRender || column.render : column.render;

  if (renderer) {
    return renderer(row, index);
  }

  if (column.key) {
    return row?.[column.key];
  }

  return "-";
};

export default function ResponsiveDataTable({
  columns,
  rows,
  getRowKey = (row, index) => row?.id ?? index,
  emptyMessage = "No records found",
  loading = false,
  minWidth = 720,
  emptyMinHeight = 180,
  mobileTitle,
  tableWrapperSx,
  rowCardSx,
  tableSx,
  tableProps,
  onRowClick
}) {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const mobileColumns = columns.filter((column) => !column.hideOnMobile);

  if (isMobile) {
    if (!loading && rows.length === 0) {
      return (
        <Card variant="outlined" sx={{ ...contentCardSx, p: 3 }}>
          <Box
            sx={{
              minHeight: emptyMinHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center"
            }}
          >
            <Typography color="text.secondary">{emptyMessage}</Typography>
          </Box>
        </Card>
      );
    }

    return (
      <Stack spacing={1.5}>
        {rows.map((row, index) => (
          <Card
            key={getRowKey(row, index)}
            variant="outlined"
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={onRowClick ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onRowClick(row); } } : undefined}
            role={onRowClick ? "button" : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            sx={{
              ...contentCardSx,
              p: 2,
              borderRadius: 2,
              ...rowCardSx,
              ...(onRowClick ? { cursor: "pointer", "&:focus-visible": { outline: "2px solid #1a73e8" } } : {})
            }}
          >
            {mobileTitle ? (
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 1.5, ...textWrapSx }}
              >
                {mobileTitle(row, index)}
              </Typography>
            ) : null}

            <Stack spacing={1.25}>
              {mobileColumns.map((column) => {
                const value = getColumnValue(column, row, index, true);

                return (
                  <Box
                    key={column.key || column.label}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 104px) minmax(0, 1fr)",
                      gap: 1,
                      alignItems: "start"
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 700,
                        letterSpacing: 0.2
                      }}
                    >
                      {column.mobileLabel || column.label}
                    </Typography>

                    <Box
                      sx={{
                        ...textWrapSx,
                        ...(column.isActions ? mobileActionGroupSx : {}),
                        ...(column.mobileValueSx || {})
                      }}
                    >
                      {value ?? "-"}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <ResponsiveTableWrapper minWidth={minWidth} sx={tableWrapperSx}>
      <Table
        {...tableProps}
        sx={{
          tableLayout: "auto",
          width: "100%",
          ...tableSx
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key || column.label}
                align={column.align}
                sx={column.headCellSx}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ height: emptyMinHeight }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <TableRow key={getRowKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onRowClick(row); } } : undefined}
                role={onRowClick ? "button" : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                sx={onRowClick ? { cursor: "pointer", "&:focus-visible": { outline: "2px solid #1a73e8" } } : undefined}>
                {columns.map((column) => (
                  <TableCell
                    key={column.key || column.label}
                    align={column.align}
                    sx={{
                      ...textWrapSx,
                      ...(column.desktopCellSx || {})
                    }}
                  >
                    {getColumnValue(column, row, index, false) ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </ResponsiveTableWrapper>
  );
}
